import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import MentionTextarea from '../components/MentionTextarea';
import DatePickerPopover from '../components/DatePickerPopover';
import SchedulePickerCard from '../components/tickets/SchedulePickerCard';
import ExpensesCard from '../components/tickets/ExpensesCard';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  MessageSquare,
  Send,
  Clock,
  Loader2,
  CheckCircle2,
  Circle,
  User,
  MapPin,
  Calendar,
  DollarSign,
  Package,
  Plus,
  Trash2,
  Receipt,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  TICKET_STATUSES,
  TICKET_CATEGORIES,
  URGENCY_LEVELS,
  formatCurrency,
  formatDate,
} from '../lib/constants';

const HIGH_VALUE_THRESHOLD = 5_000_000;

// Mirror of backend allowed categories (label + target handler role for hint)
const REASSIGN_CATEGORIES = [
  { value: 'umum', label: 'Umum', role: 'admin_purchasing' },
  { value: 'me', label: 'ME (Mechanical/Electrical)', role: 'admin_maintenance' },
  { value: 'it', label: 'IT', role: 'admin_it' },
  { value: 'utility_payment', label: 'Utility Payment', role: 'finance' },
  { value: 'transfer_operasional', label: 'Transfer Operasional', role: 'finance' },
  { value: 'petty_cash_in', label: 'Petty Cash (IN)', role: 'finance' },
  { value: 'petty_cash_out', label: 'Petty Cash (OUT)', role: 'finance' },
  { value: 'deposit_in', label: 'Deposit (IN)', role: 'finance' },
  { value: 'deposit_out', label: 'Deposit (OUT)', role: 'finance' },
];

const STEPPER_STEPS = [
  { key: 'draft', label: 'Diajukan' },
  { key: 'pending_approval', label: 'Approval' },
  { key: 'approved', label: 'Disetujui' },
  { key: 'in_progress', label: 'Pengerjaan' },
  { key: 'waiting_confirmation', label: 'Konfirmasi' },
  { key: 'completed', label: 'Selesai' },
  { key: 'invoiced', label: 'Invoice' },
];

/** Render comment text with @role_code highlighted. */
const renderMentions = (text) => {
  if (!text) return null;
  const parts = text.split(/(@[a-zA-Z0-9_]+)/g);
  return parts.map((p, i) => {
    if (p.startsWith('@')) {
      return (
        <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium text-xs mx-0.5">
          {p}
        </span>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

const TicketDetailPage = () => {
  const { ticketId } = useParams();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showAllocDialog, setShowAllocDialog] = useState(false);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [allocForm, setAllocForm] = useState({ item_id: '', quantity: 1, from_location: 'PUSAT' });
  const [allocating, setAllocating] = useState(false);
  const [editingAlloc, setEditingAlloc] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [scheduleForm, setScheduleForm] = useState({ scheduled_start: '', scheduled_end: '', schedule_note: '' });
  const [scheduleLoad, setScheduleLoad] = useState({ count: 0, tickets: [] });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignForm, setReassignForm] = useState({ new_category: '', note: '' });
  const [reassigning, setReassigning] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ description: '', vendor: '', amount: '', receipt_url: '', receipt_filename: '', note: '' });
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  useEffect(() => {
    loadTicket();
    loadItems();
    loadExpenses();
    // Mark this ticket's mention notifications as read for the current user
    api.post('/notifications/mark-read', { ticket_id: ticketId }).catch(() => {});
  }, [ticketId]);

  const loadExpenses = async () => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}/expenses`);
      setExpenses(data);
    } catch (_) {
      setExpenses([]);
    }
  };

  const handleSaveAllocEdit = async (alloc) => {
    const qty = parseInt(editQty);
    if (!qty || qty < 1) { toast.error('Jumlah harus > 0'); return; }
    if (qty === alloc.quantity) { setEditingAlloc(null); return; }
    try {
      await api.put(`/tickets/${ticketId}/allocations/${alloc.id}`, {
        item_id: alloc.item_id, quantity: qty, from_location: alloc.from_location,
      });
      toast.success('Alokasi diperbarui');
      setEditingAlloc(null);
      loadTicket();
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal update');
    }
  };

  const handleDeleteAlloc = async (alloc) => {
    if (!window.confirm(`Hapus alokasi ${alloc.quantity} × ${alloc.item_name || 'barang'}? Stok dikembalikan.`)) return;
    try {
      await api.delete(`/tickets/${ticketId}/allocations/${alloc.id}`);
      toast.success('Alokasi dihapus');
      loadTicket();
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal hapus');
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put(`/tickets/${ticketId}/schedule`, {
        scheduled_start: scheduleForm.scheduled_start || null,
        scheduled_end: scheduleForm.scheduled_end || null,
        schedule_note: scheduleForm.schedule_note || null,
      });
      toast.success('Jadwal disimpan');
      loadTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal simpan jadwal');
    } finally {
      setSavingSchedule(false);
    }
  };

  useEffect(() => {
    if (ticket) {
      const today = new Date().toISOString().slice(0, 10);
      setScheduleForm({
        scheduled_start: ticket.scheduled_start || today,
        scheduled_end: ticket.scheduled_end || '',
        schedule_note: ticket.schedule_note || '',
      });
    }
  }, [ticket]);

  // Fetch schedule load for the picked start date (maintenance + IT categories only)
  useEffect(() => {
    const raw = scheduleForm.scheduled_start;
    if (!raw || !ticket || !['umum', 'me', 'it'].includes(ticket.category)) {
      setScheduleLoad({ count: 0, tickets: [] });
      return;
    }
    const start = String(raw).slice(0, 10);
    const nextDay = new Date(new Date(`${start}T00:00:00Z`).getTime() + 86400000)
      .toISOString().slice(0, 10);
    api.get(`/tickets/schedule/calendar?start=${start}&end=${nextDay}`)
      .then((r) => {
        const list = (r.data || []).filter((t) => (
          t.ticket_number !== ticketId &&
          ['umum', 'me', 'it'].includes(t.category) &&
          (t.scheduled_start || '').slice(0, 10) === start
        ));
        setScheduleLoad({ count: list.length, tickets: list });
      })
      .catch(() => setScheduleLoad({ count: 0, tickets: [] }));
  }, [scheduleForm.scheduled_start, ticket, ticketId]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    const amt = parseFloat(expenseForm.amount);
    if (!expenseForm.description.trim() || !amt || amt <= 0) {
      toast.error('Isi deskripsi dan nominal > 0');
      return;
    }
    try {
      await api.post(`/tickets/${ticketId}/expenses`, {
        description: expenseForm.description.trim(),
        vendor: expenseForm.vendor.trim() || undefined,
        amount: amt,
        receipt_url: expenseForm.receipt_url.trim() || undefined,
        note: expenseForm.note.trim() || undefined,
      });
      toast.success('Pembelian langsung ditambahkan');
      setShowExpenseDialog(false);
      setExpenseForm({ description: '', vendor: '', amount: '', receipt_url: '', receipt_filename: '', note: '' });
      loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal tambah pengeluaran');
    }
  };

  const handleUploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 8MB');
      return;
    }
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/uploads/receipt', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setExpenseForm((f) => ({ ...f, receipt_url: data.url, receipt_filename: data.filename }));
      toast.success('File berhasil diunggah');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal upload');
    } finally {
      setUploadingReceipt(false);
      e.target.value = '';
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (!window.confirm(`Hapus pengeluaran "${expense.description}"?`)) return;
    try {
      await api.delete(`/tickets/${ticketId}/expenses/${expense.id}`);
      toast.success('Pengeluaran dihapus');
      loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal hapus');
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const canAddExpense = ticket && ticket.status !== 'invoiced' && ticket.status !== 'rejected';

  const loadItems = async () => {
    try {
      const [itemsRes, locationsRes, stocksRes] = await Promise.all([
        api.get('/items', { params: { include_all: true } }),
        api.get('/locations'),
        api.get('/warehouse/stock'),
      ]);
      setItems(itemsRes.data);
      setLocations(locationsRes.data);
      setStocks(stocksRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadTicket = async () => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setTicket(data);
    } catch (error) {
      toast.error('Tiket tidak ditemukan');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/tickets/${ticketId}/comments`, { comment });
      setComment('');
      await loadTicket();
      toast.success('Komentar ditambahkan');
    } catch (error) {
      toast.error('Gagal menambahkan komentar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setUpdatingStatus(true);
    try {
      await api.put(`/tickets/${ticketId}/status`, { status: newStatus });
      await loadTicket();
      setNewStatus('');
      toast.success('Status berhasil diubah');
    } catch (error) {
      toast.error('Gagal mengubah status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAllocate = async () => {
    if (!allocForm.item_id || allocForm.quantity < 1) return;
    setAllocating(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/allocate-item`, {
        item_id: allocForm.item_id,
        quantity: parseInt(allocForm.quantity),
        from_location: allocForm.from_location,
      });
      toast.success(`${data.item_name} × ${data.quantity} = ${formatCurrency(data.subtotal)}`);
      setShowAllocDialog(false);
      setAllocForm({ item_id: '', quantity: 1, from_location: 'PUSAT' });
      await loadTicket();
      await loadItems(); // Refresh stock
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal alokasi barang');
    } finally {
      setAllocating(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Setujui tiket ini?')) return;
    try {
      await api.post(`/tickets/${ticketId}/approve`);
      toast.success('Tiket disetujui');
      await loadTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal approve');
    }
  };

  const handleReassignCategory = async () => {
    if (!reassignForm.new_category) {
      toast.error('Pilih kategori tujuan');
      return;
    }
    if (reassignForm.new_category === ticket.category) {
      toast.error('Kategori tujuan sama dengan kategori saat ini');
      return;
    }
    setReassigning(true);
    try {
      const { data } = await api.put(`/tickets/${ticketId}/category`, {
        new_category: reassignForm.new_category,
        note: reassignForm.note || undefined,
      });
      toast.success(data.message || 'Kategori dialihkan');
      setShowReassignDialog(false);
      setReassignForm({ new_category: '', note: '' });
      await loadTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengalihkan kategori');
    } finally {
      setReassigning(false);
    }
  };

  const canReassignCategory = () => {
    if (!user || !ticket) return false;
    if (['completed', 'invoiced', 'rejected', 'cancelled'].includes(ticket.status)) return false;
    return hasPermission('reassign_ticket_category');
  };

  const isHighValue = !!(ticket && (ticket.estimated_cost || 0) > HIGH_VALUE_THRESHOLD);
  const canApproveHighValue = hasPermission('approve_high_value');
  const approveBlockedByValue = isHighValue && !canApproveHighValue;

  const handleSubmitForApproval = async () => {
    if (!window.confirm('Ajukan tiket ini untuk approval? Setelah dikirim, admin dapat melihat & meninjau tiket.')) return;
    try {
      await api.put(`/tickets/${ticketId}/status`, { status: 'pending_approval' });
      toast.success('Tiket diajukan untuk approval');
      await loadTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengajukan tiket');
    }
  };

  const canSubmitDraft = () => {
    if (!user || !ticket) return false;
    return ticket.status === 'draft' && ticket.created_by === user.id;
  };

  const canDeleteTicket = () => {
    if (!user || !ticket) return false;
    if (ticket.status === 'draft') {
      return ticket.created_by === user.id;
    }
    if (['completed', 'rejected', 'cancelled'].includes(ticket.status)) {
      return user.role === 'super_admin';
    }
    return false;
  };

  const handleDeleteTicket = async () => {
    const label = ticket.status === 'draft' ? 'buang draft' : 'hapus tiket dari riwayat';
    if (!window.confirm(`Yakin ingin ${label} "${ticket.ticket_number}"? Aksi ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success('Tiket berhasil dihapus');
      navigate('/tickets');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus tiket');
    }
  };

  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ service_cost: '', notes: '' });
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  const canGenerateInvoice = () => {
    if (!user || !ticket) return false;
    if (ticket.status !== 'completed') return false;
    if ((ticket.invoices || []).length > 0) return false;
    return ['super_admin', 'finance', 'admin_maintenance'].includes(user.role);
  };

  const materialTotal = (ticket?.items_used || []).reduce(
    (sum, i) => sum + (i.quantity * i.price),
    0
  );

  const handleGenerateInvoice = async () => {
    const serviceCost = parseFloat(invoiceForm.service_cost) || 0;
    setGeneratingInvoice(true);
    try {
      await api.post('/invoices/generate', {
        ticket_id: ticketId,
        service_cost: serviceCost,
        notes: invoiceForm.notes,
      });
      toast.success('Invoice berhasil dibuat');
      setShowInvoiceDialog(false);
      setInvoiceForm({ service_cost: '', notes: '' });
      await loadTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Alasan penolakan:');
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/tickets/${ticketId}/reject`, { reason });
      toast.success('Tiket ditolak');
      await loadTicket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reject');
    }
  };

  const canApproveOrReject = () => {
    if (!user || !ticket) return false;
    // Only show approve/reject when ticket has been submitted (pending_approval)
    if (ticket.status !== 'pending_approval') return false;
    return ['super_admin', 'admin_maintenance', 'admin_it', 'admin_purchasing', 'finance'].includes(user.role);
  };

  // Map ticket category → allowed handler role
  const CATEGORY_HANDLER_ROLE = {
    umum: 'admin_purchasing',
    me: 'admin_maintenance',
    it: 'admin_it',
    petty_cash: 'finance',                // deprecated (tiket lama)
    refund_deposit: 'finance',            // deprecated
    utility_payment: 'finance',
    transfer_operasional: 'finance',
    petty_cash_in: 'finance',
    petty_cash_out: 'finance',
    deposit_in: 'finance',
    deposit_out: 'finance',
  };

  // Determine next-step action button based on current status
  const getNextStepAction = () => {
    if (!user || !ticket) return null;
    const handlerRole = CATEGORY_HANDLER_ROLE[ticket.category];
    const isSuperAdmin = user.role === 'super_admin';
    const isHandler = user.role === handlerRole || isSuperAdmin;
    const isCreator = user.id === ticket.created_by;

    if (ticket.status === 'approved' && isHandler) {
      return { label: 'Mulai Pengerjaan', next: 'in_progress' };
    }
    if (ticket.status === 'in_progress' && isHandler) {
      return { label: 'Kirim untuk Konfirmasi', next: 'waiting_confirmation' };
    }
    if (ticket.status === 'waiting_confirmation' && (isCreator || isSuperAdmin || isHandler)) {
      return { label: 'Konfirmasi Selesai', next: 'completed' };
    }
    return null;
  };

  const handleNextStep = async () => {
    const action = getNextStepAction();
    if (!action) return;
    if (!window.confirm(`${action.label}?`)) return;
    setUpdatingStatus(true);
    try {
      await api.put(`/tickets/${ticketId}/status`, { status: action.next });
      await loadTicket();
      toast.success(`Status: ${action.label}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const canAllocateItems = () => {
    if (!user) return false;
    return ['super_admin', 'admin_gudang', 'admin_maintenance', 'admin_purchasing'].includes(user.role);
  };

  const getStockAt = (itemId, locationId) => {
    const stock = stocks.find((s) => s.item_id === itemId && s.location_id === locationId);
    return stock?.quantity || 0;
  };

  const canUpdateStatus = () => {
    if (!user || !ticket) return false;
    if (['completed', 'invoiced', 'rejected', 'cancelled'].includes(ticket.status)) return false;
    if (user.role === 'super_admin') return true;
    const handlerRole = CATEGORY_HANDLER_ROLE[ticket.category];
    if (handlerRole && user.role === handlerRole) return true;
    if (ticket.status === 'waiting_confirmation' && user.id === ticket.created_by) return true;
    return false;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  };

  const getStepStatus = (stepKey) => {
    if (!ticket) return 'pending';
    const currentStep = TICKET_STATUSES[ticket.status]?.step || 0;
    const stepIndex = STEPPER_STEPS.findIndex((s) => s.key === stepKey);
    const currentIndex = STEPPER_STEPS.findIndex((s) => s.key === ticket.status);

    if (ticket.status === 'rejected' || ticket.status === 'cancelled') {
      return stepIndex === 0 ? 'completed' : 'pending';
    }

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!ticket) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="ticket-detail-page">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/tickets')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Kembali
        </Button>

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="font-mono text-sm bg-secondary px-3 py-1 rounded" data-testid="ticket-number">
              {ticket.ticket_number}
            </span>
            <Badge variant="outline" className={URGENCY_LEVELS[ticket.urgency]?.color}>
              {URGENCY_LEVELS[ticket.urgency]?.label}
            </Badge>
            <Badge variant="secondary">{TICKET_CATEGORIES[ticket.category]}</Badge>
            <Badge className={TICKET_STATUSES[ticket.status]?.color}>
              {TICKET_STATUSES[ticket.status]?.label}
            </Badge>
          </div>
          <h1 className="text-3xl font-medium tracking-tight mb-2">{ticket.title}</h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {ticket.creator_name}
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {ticket.location_name}
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(ticket.created_at)}
            </div>
          </div>
        </div>

        {/* Progress Stepper */}
        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg tracking-tight font-medium">Progress Tiket</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between overflow-x-auto pb-2">
              {STEPPER_STEPS.map((step, idx) => {
                const status = getStepStatus(step.key);
                return (
                  <div key={step.key} className="flex items-start flex-1 min-w-[100px]">
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                          status === 'completed'
                            ? 'bg-primary border-primary text-primary-foreground'
                            : status === 'current'
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-secondary border-border text-muted-foreground'
                        }`}
                      >
                        {status === 'completed' ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : status === 'current' ? (
                          <Clock className="w-5 h-5" />
                        ) : (
                          <Circle className="w-5 h-5" />
                        )}
                      </div>
                      <p
                        className={`text-xs mt-2 text-center whitespace-nowrap ${
                          status === 'current' ? 'font-medium text-foreground' : 'text-muted-foreground'
                        }`}
                      >
                        {step.label}
                      </p>
                    </div>
                    {idx < STEPPER_STEPS.length - 1 && (
                      <div
                        className={`h-0.5 flex-1 mt-5 ${
                          status === 'completed' ? 'bg-primary' : 'bg-border'
                        }`}
                        style={{ minWidth: '20px' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg tracking-tight font-medium">Deskripsi</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-foreground whitespace-pre-wrap leading-relaxed">
                  {ticket.description}
                </p>
              </CardContent>
            </Card>

            {/* Items Used - Barang Dialokasi ke Tiket */}
            <Card className="border-border">
              <CardHeader className="border-b border-border flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg tracking-tight font-medium flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Barang Digunakan ({ticket.items_used?.length || 0})
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">Barang keluar dari gudang untuk tiket ini</p>
                </div>
                {canAllocateItems() && (
                  <Dialog open={showAllocDialog} onOpenChange={setShowAllocDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="allocate-item-button">
                        <Plus className="w-3 h-3 mr-1" />
                        Alokasi Barang
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Alokasi Barang ke Tiket</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Dari Lokasi</Label>
                          <Select
                            value={allocForm.from_location}
                            onValueChange={(v) => setAllocForm({ ...allocForm, from_location: v })}
                          >
                            <SelectTrigger data-testid="alloc-location-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {locations.map((loc) => (
                                <SelectItem key={loc.id} value={loc.id}>
                                  {loc.name} {loc.is_warehouse ? '(Gudang)' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Default dari Gudang Pusat. Stok akan dikurangi otomatis.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Barang</Label>
                          <Select
                            value={allocForm.item_id}
                            onValueChange={(v) => setAllocForm({ ...allocForm, item_id: v })}
                          >
                            <SelectTrigger data-testid="alloc-item-select">
                              <SelectValue placeholder="Pilih barang" />
                            </SelectTrigger>
                            <SelectContent>
                              {items.map((item) => {
                                const stock = getStockAt(item.id, allocForm.from_location);
                                return (
                                  <SelectItem key={item.id} value={item.id} disabled={stock === 0}>
                                    {item.name} ({item.unit}) — Stok: {stock}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Jumlah</Label>
                          <Input
                            type="number"
                            min="1"
                            max={getStockAt(allocForm.item_id, allocForm.from_location)}
                            value={allocForm.quantity}
                            onChange={(e) => setAllocForm({ ...allocForm, quantity: e.target.value })}
                            data-testid="alloc-quantity-input"
                          />
                          {allocForm.item_id && (() => {
                            const it = items.find((i) => i.id === allocForm.item_id);
                            if (!it) return null;
                            const stock = getStockAt(allocForm.item_id, allocForm.from_location);
                            const subtotal = allocForm.quantity * it.price;
                            return (
                              <div className="mt-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                                <p className="text-xs text-muted-foreground mb-1">
                                  Stok tersedia: <span className="font-medium text-foreground">{stock} {it.unit}</span>
                                </p>
                                <div className="flex justify-between text-sm">
                                  <span>{allocForm.quantity} × {formatCurrency(it.price)}</span>
                                  <span className="font-medium text-primary">{formatCurrency(subtotal)}</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAllocDialog(false)}>
                          Batal
                        </Button>
                        <Button
                          onClick={handleAllocate}
                          disabled={allocating || !allocForm.item_id || allocForm.quantity < 1}
                          className="bg-primary hover:bg-primary/90"
                          data-testid="save-alloc-button"
                        >
                          {allocating ? 'Menyimpan...' : 'Alokasikan'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {ticket.items_used && ticket.items_used.length > 0 ? (
                  <>
                    <div className="divide-y divide-border">
                      {ticket.items_used.map((item) => (
                        <div key={item.id || `${item.item_id}-${item.created_at}`} className="flex items-center justify-between p-4 gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{item.item_name || 'Barang'}</p>
                            <div className="text-sm text-muted-foreground">
                              {editingAlloc === item.id ? (
                                <span className="inline-flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    value={editQty}
                                    onChange={(e) => setEditQty(e.target.value)}
                                    className="h-7 w-20 inline-block"
                                    data-testid={`edit-alloc-qty-${item.id}`}
                                  />
                                  <span>{item.unit || 'unit'} × {formatCurrency(item.price)}</span>
                                </span>
                              ) : (
                                <>{item.quantity} {item.unit || 'unit'} × {formatCurrency(item.price)}</>
                              )}
                              {item.from_location && (
                                <span className="ml-2 text-xs">
                                  <Badge variant="outline" className="font-mono text-xs">{item.from_location}</Badge>
                                </span>
                              )}
                            </div>
                            {item.receipt_url && (
                              <a
                                href={item.receipt_url.startsWith('http') ? item.receipt_url : `${process.env.REACT_APP_BACKEND_URL}${item.receipt_url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-primary hover:underline mt-1 inline-block"
                                data-testid={`view-pr-receipt-${item.id}`}
                              >
                                Lihat tanda terima →
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium whitespace-nowrap">
                              {formatCurrency((editingAlloc === item.id ? (parseInt(editQty) || 0) : item.quantity) * item.price)}
                            </p>
                            {canAllocateItems() && ticket.status !== 'invoiced' && (
                              item.source === 'purchase_request' ? (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] bg-blue-50 text-blue-700 border-blue-300"
                                  title="Alokasi ini bersumber dari Purchase Request yang sudah diterima — dikunci untuk konsistensi dengan tanda terima"
                                  data-testid={`alloc-locked-${item.id}`}
                                >
                                  Terkunci (dari PR)
                                </Badge>
                              ) : (
                              <>
                                {editingAlloc === item.id ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => handleSaveAllocEdit(item)}
                                      data-testid={`save-alloc-edit-${item.id}`}
                                    >
                                      Simpan
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => setEditingAlloc(null)}
                                    >
                                      Batal
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => { setEditingAlloc(item.id); setEditQty(String(item.quantity)); }}
                                      className="text-xs text-primary hover:underline"
                                      data-testid={`edit-alloc-${item.id}`}
                                    >
                                      Ubah
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteAlloc(item)}
                                      className="text-xs text-destructive hover:underline"
                                      data-testid={`delete-alloc-${item.id}`}
                                    >
                                      Hapus
                                    </button>
                                  </>
                                )}
                              </>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between p-4 bg-secondary/50 border-t border-border">
                      <span className="font-semibold text-sm">Total Barang</span>
                      <span className="font-medium text-primary">
                        {formatCurrency(ticket.items_used.reduce((s, i) => s + i.quantity * i.price, 0))}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Belum ada barang dialokasi</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Direct Expenses (out-of-pocket purchases / reimbursement) */}
            <ExpensesCard
              expenses={expenses}
              canAddExpense={!!canAddExpense}
              onAdd={() => setShowExpenseDialog(true)}
              onDelete={handleDeleteExpense}
              currentUserId={user?.id}
              currentUserRole={user?.role}
            />

            {/* Comments */}
            <Card className="border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg tracking-tight font-medium flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Diskusi ({ticket.comments?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {ticket.comments && ticket.comments.length > 0 ? (
                  <div className="space-y-4">
                    {ticket.comments.map((c, idx) => (
                      <div key={idx} className="flex gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                            {getInitials(c.user_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-sm">{c.user_name}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(c.created_at)}</p>
                          </div>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{renderMentions(c.comment)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Belum ada komentar
                  </p>
                )}

                <form onSubmit={handleAddComment} className="border-t border-border pt-4">
                  <MentionTextarea
                    placeholder="Tulis komentar... (ketik @ untuk ping role, contoh: @admin_it)"
                    value={comment}
                    onChange={setComment}
                    rows={3}
                    testId="comment-input"
                  />
                  <Button
                    type="submit"
                    disabled={submitting || !comment.trim()}
                    className="mt-3 bg-primary hover:bg-primary/90"
                    size="sm"
                    data-testid="submit-comment-button"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? 'Mengirim...' : 'Kirim Komentar'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Linked Invoice(s) */}
            {(ticket.invoices || []).length > 0 && (
              <Card className="border-emerald-300 bg-emerald-50/40">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg tracking-tight font-medium flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-emerald-700" />
                    Invoice Tiket
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {ticket.invoices.map((inv) => (
                    <div key={inv.id} className="space-y-2 pb-3 border-b border-border last:border-0 last:pb-0" data-testid={`ticket-invoice-${inv.id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-mono text-xs">{inv.invoice_number}</span>
                        <Badge
                          className={
                            inv.status === 'paid'
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]'
                              : 'bg-amber-100 text-amber-800 border-amber-300 text-[10px]'
                          }
                        >
                          {inv.status === 'paid' ? 'Sudah Dibayar' : 'Belum Dibayar'}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div className="flex justify-between"><span>Jasa</span><span>{formatCurrency(inv.service_cost || 0)}</span></div>
                        <div className="flex justify-between"><span>Barang</span><span>{formatCurrency(inv.material_cost || 0)}</span></div>
                        {(inv.direct_cost || 0) > 0 && (
                          <div className="flex justify-between"><span>Pembelian langsung</span><span>{formatCurrency(inv.direct_cost)}</span></div>
                        )}
                        <div className="flex justify-between font-semibold text-foreground pt-1 border-t border-border">
                          <span>Total</span><span>{formatCurrency(inv.total_cost || 0)}</span>
                        </div>
                      </div>
                      <Link
                        to="/invoices"
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                        data-testid={`ticket-invoice-link-${inv.id}`}
                      >
                        <FileText className="w-3 h-3" />
                        Buka di halaman Invoice
                      </Link>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Generate Invoice (when ticket completed & no invoice yet) */}
            {canGenerateInvoice() && (
              <Card className="border-primary/40 bg-primary/5">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg tracking-tight font-medium">Terbitkan Invoice</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Tiket sudah selesai. Terbitkan invoice untuk melanjutkan proses pembayaran.
                  </p>
                  <Button
                    onClick={() => setShowInvoiceDialog(true)}
                    className="w-full bg-primary hover:bg-primary/90"
                    size="sm"
                    data-testid="generate-invoice-ticket-button"
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    Generate Invoice
                  </Button>
                </CardContent>
              </Card>
            )}
            {/* Submit Draft for Approval (creator only) */}
            {canSubmitDraft() && (
              <Card className="border-primary/40 bg-primary/5">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg tracking-tight font-medium">Tiket Draft</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Tiket ini masih draft dan hanya bisa dilihat oleh Anda. Ajukan untuk approval agar admin dapat meninjau.
                  </p>
                  <Button
                    onClick={handleSubmitForApproval}
                    className="w-full bg-primary hover:bg-primary/90"
                    size="sm"
                    data-testid="submit-draft-button"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Ajukan untuk Approval
                  </Button>
                  <Button
                    onClick={handleDeleteTicket}
                    variant="outline"
                    className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    size="sm"
                    data-testid="delete-draft-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Buang Draft
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Delete from history (super_admin, terminal statuses) */}
            {!canSubmitDraft() && canDeleteTicket() && (
              <Card className="border-destructive/40">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg tracking-tight font-medium">Hapus Tiket</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Tiket ini di riwayat. Menghapus akan menghilangkannya beserta log, komentar, dan alokasi barang secara permanen.
                  </p>
                  <Button
                    onClick={handleDeleteTicket}
                    variant="outline"
                    className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    size="sm"
                    data-testid="delete-history-ticket-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Hapus Permanen
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Reject Reason - highly visible when ticket is rejected */}
            {ticket.status === 'rejected' && ticket.reject_reason && (
              <Card className="border-destructive/60 bg-destructive/5">
                <CardHeader className="border-b border-destructive/40">
                  <CardTitle className="text-lg tracking-tight font-medium flex items-center gap-2 text-destructive">
                    Tiket Ditolak
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Alasan Penolakan</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap" data-testid="reject-reason-display">
                    {ticket.reject_reason}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Schedule Card - manage work schedule */}
            {['approved', 'in_progress', 'waiting_confirmation'].includes(ticket.status) && (
              <SchedulePickerCard
                ticket={ticket}
                ticketId={ticketId}
                scheduleForm={scheduleForm}
                setScheduleForm={setScheduleForm}
                scheduleLoad={scheduleLoad}
                onSave={handleSaveSchedule}
                saving={savingSchedule}
                canEdit={hasPermission('manage_ticket_workflow')}
              />
            )}

            {/* Approve / Reject Actions */}
            {canApproveOrReject() && (
              <Card className="border-border">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg tracking-tight font-medium">Aksi Approval</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-2">
                  {isHighValue && (
                    <div
                      className={`text-xs p-2 rounded border ${approveBlockedByValue ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-blue-50 border-blue-200 text-blue-900'}`}
                      data-testid="high-value-notice"
                    >
                      <strong>Nilai Tinggi:</strong> Estimasi {formatCurrency(ticket.estimated_cost || 0)} melebihi batas {formatCurrency(HIGH_VALUE_THRESHOLD)}.
                      {approveBlockedByValue
                        ? ' Anda tidak memiliki izin approve nilai tinggi.'
                        : ' Anda memiliki izin approve nilai tinggi.'}
                    </div>
                  )}
                  <Button
                    onClick={handleApprove}
                    className="w-full bg-primary hover:bg-primary/90"
                    size="sm"
                    disabled={approveBlockedByValue}
                    title={approveBlockedByValue ? 'Diperlukan izin approve_high_value untuk menyetujui tiket ini' : undefined}
                    data-testid="approve-ticket-button"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Setujui Tiket
                  </Button>
                  <Button
                    onClick={handleReject}
                    variant="outline"
                    className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    size="sm"
                    data-testid="reject-ticket-button"
                  >
                    Tolak Tiket
                  </Button>
                  {ticket.reject_reason && (
                    <p className="text-xs text-muted-foreground italic pt-2">
                      Alasan: {ticket.reject_reason}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Reassign Category */}
            {canReassignCategory() && (
              <Card className="border-border">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg tracking-tight font-medium">Alihkan Kategori</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Alihkan tiket ke departemen lain. Sistem akan otomatis nge-ping role kategori baru di diskusi.
                  </p>
                  <div className="text-xs flex items-center gap-1.5">
                    <span>Kategori sekarang:</span>
                    <Badge variant="secondary">{TICKET_CATEGORIES[ticket.category] || ticket.category}</Badge>
                  </div>
                  <Button
                    onClick={() => { setReassignForm({ new_category: '', note: '' }); setShowReassignDialog(true); }}
                    variant="outline"
                    className="w-full"
                    size="sm"
                    data-testid="open-reassign-category-button"
                  >
                    Alihkan Kategori
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Next Step Action (context-aware) */}
            {getNextStepAction() && (
              <Card className="border-border">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg tracking-tight font-medium">Aksi Lanjutan</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-2">
                  <Button
                    onClick={handleNextStep}
                    disabled={updatingStatus}
                    className="w-full bg-primary hover:bg-primary/90"
                    size="sm"
                    data-testid="next-step-button"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {updatingStatus ? 'Menyimpan...' : getNextStepAction().label}
                  </Button>
                  {ticket.status === 'waiting_confirmation' && user?.id !== ticket.created_by && user?.role !== 'super_admin' && (
                    <p className="text-xs text-muted-foreground italic pt-2">
                      Menunggu konfirmasi dari pembuat tiket
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Cost Info */}
            <Card className="border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg tracking-tight font-medium flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Biaya
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Estimasi</span>
                  <span className="font-medium">{formatCurrency(ticket.estimated_cost)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Realisasi</span>
                  <span className="font-medium">{formatCurrency(ticket.actual_cost)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Status Update */}
            {canUpdateStatus() && (
              <Card className="border-border">
                <CardHeader className="border-b border-border">
                  <CardTitle className="text-lg tracking-tight font-medium">Ubah Status</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="new-status-select">
                      <SelectValue placeholder="Pilih status baru" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TICKET_STATUSES).map(([value, { label }]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleUpdateStatus}
                    disabled={!newStatus || updatingStatus}
                    className="w-full bg-primary hover:bg-primary/90"
                    size="sm"
                    data-testid="update-status-button"
                  >
                    {updatingStatus ? 'Menyimpan...' : 'Ubah Status'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Status History */}
            <Card className="border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg tracking-tight font-medium">Riwayat Status</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {ticket.status_logs && ticket.status_logs.length > 0 ? (
                  <div className="space-y-3">
                    {ticket.status_logs.map((log, idx) => (
                      <div key={idx} className="flex gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs">
                            {TICKET_STATUSES[log.new_status]?.label || log.new_status}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {log.changed_by_name || 'System'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(log.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    Belum ada perubahan status
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Generate Invoice Dialog */}
        <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Generate Invoice — {ticket?.ticket_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="text-sm space-y-1 p-3 rounded-md bg-secondary/50">
                <div className="flex justify-between text-muted-foreground">
                  <span>Total Barang Digunakan</span>
                  <span className="font-medium text-foreground">{formatCurrency(materialTotal)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Biaya Jasa / Service (Rp)</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={invoiceForm.service_cost}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, service_cost: e.target.value })}
                  data-testid="invoice-service-cost-input"
                />
                <p className="text-xs text-muted-foreground">Isi 0 jika tidak ada biaya jasa/tenaga kerja.</p>
              </div>
              <div className="space-y-2">
                <Label>Catatan</Label>
                <Textarea
                  placeholder="Catatan tambahan untuk invoice..."
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                  data-testid="invoice-notes-input"
                />
              </div>
              <div className="p-3 rounded-md bg-primary/5 border border-primary/20 space-y-1 text-sm">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Barang (alokasi gudang)</span>
                  <span>{formatCurrency(materialTotal)}</span>
                </div>
                {totalExpenses > 0 && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Pembelian langsung</span>
                    <span>{formatCurrency(totalExpenses)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Jasa/tenaga kerja</span>
                  <span>{formatCurrency(parseFloat(invoiceForm.service_cost) || 0)}</span>
                </div>
                <div className="flex justify-between border-t border-primary/20 pt-1">
                  <span className="font-medium">Total Invoice</span>
                  <span className="font-bold text-primary">
                    {formatCurrency(materialTotal + totalExpenses + (parseFloat(invoiceForm.service_cost) || 0))}
                  </span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Batal</Button>
              <Button
                onClick={handleGenerateInvoice}
                disabled={generatingInvoice}
                className="bg-primary hover:bg-primary/90"
                data-testid="confirm-generate-invoice-button"
              >
                {generatingInvoice ? 'Membuat...' : 'Terbitkan Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reassign Category Dialog */}
        <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Alihkan Kategori Tiket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="text-xs p-2 rounded bg-secondary/60 border border-border">
                Tiket <span className="font-mono">{ticket.ticket_number}</span> — kategori saat ini: <strong>{TICKET_CATEGORIES[ticket.category] || ticket.category}</strong>
              </div>
              <div className="space-y-2">
                <Label>Kategori Tujuan *</Label>
                <Select
                  value={reassignForm.new_category}
                  onValueChange={(v) => setReassignForm({ ...reassignForm, new_category: v })}
                >
                  <SelectTrigger data-testid="reassign-category-select">
                    <SelectValue placeholder="Pilih kategori baru" />
                  </SelectTrigger>
                  <SelectContent>
                    {REASSIGN_CATEGORIES.filter((c) => c.value !== ticket.category).map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label} <span className="text-xs text-muted-foreground">(@{c.role})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Sistem akan otomatis mention role handler kategori baru di diskusi.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Catatan (opsional)</Label>
                <Textarea
                  placeholder="Alasan pengalihan, misal: 'terkait server, bukan kelistrikan'"
                  value={reassignForm.note}
                  onChange={(e) => setReassignForm({ ...reassignForm, note: e.target.value })}
                  rows={2}
                  data-testid="reassign-note-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReassignDialog(false)}>Batal</Button>
              <Button
                onClick={handleReassignCategory}
                disabled={reassigning || !reassignForm.new_category}
                className="bg-primary hover:bg-primary/90"
                data-testid="confirm-reassign-category-button"
              >
                {reassigning ? 'Mengalihkan...' : 'Alihkan Kategori'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Direct Expense Dialog */}
        <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Tambah Pembelian Langsung</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddExpense} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Deskripsi Barang / Jasa *</Label>
                <Input
                  placeholder="Contoh: Bohlam LED 10W (3 pcs)"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  data-testid="expense-description-input"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Vendor / Toko</Label>
                  <Input
                    placeholder="Contoh: Ace Hardware"
                    value={expenseForm.vendor}
                    onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value })}
                    data-testid="expense-vendor-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nominal (Rp) *</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="150000"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    data-testid="expense-amount-input"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Bukti / Nota (opsional)</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                    onChange={handleUploadReceipt}
                    disabled={uploadingReceipt}
                    className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                    data-testid="expense-receipt-input"
                  />
                  {uploadingReceipt && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                </div>
                {expenseForm.receipt_url && (
                  <div className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-2 py-1.5">
                    <span className="truncate">✓ {expenseForm.receipt_filename || 'File terlampir'}</span>
                    <button
                      type="button"
                      onClick={() => setExpenseForm({ ...expenseForm, receipt_url: '', receipt_filename: '' })}
                      className="text-destructive hover:underline"
                    >
                      Hapus
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">Format: PDF, JPG, PNG, WebP (max 8MB)</p>
              </div>
              <div className="space-y-2">
                <Label>Catatan</Label>
                <Textarea
                  placeholder="Catatan tambahan (opsional)"
                  value={expenseForm.note}
                  onChange={(e) => setExpenseForm({ ...expenseForm, note: e.target.value })}
                  rows={2}
                  data-testid="expense-note-input"
                />
              </div>
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
                Nominal ini akan otomatis dimasukkan ke <strong>invoice akhir</strong> tiket saat Anda menekan &quot;Terbitkan Invoice&quot;.
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowExpenseDialog(false)}>Batal</Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="save-expense-button">
                  Simpan Pembelian
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TicketDetailPage;
